import { useQuery } from '@tanstack/react-query';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from 'src/auth/user-context';
import { Spinner } from 'src/common/components/Spinner';
import { useModal } from 'src/common/components/Modal';
import { useCompaniesQuery } from 'src/company/queries';
import { getAllProjects } from 'src/project/api/projectsApi';
import { ProjectListActionBar } from './ActionBar';
import { ProjectListCompanySection } from './CompanySection';
import {
  filterAndSortProjects,
  filterProjectsByTypeAndStatus,
  getProjectFilterOptions,
  groupProjectsByCompany,
} from './utils';
import { CreateFirstProjectModal } from '../CreateProject/CreateFirstProjectModal';
import { CreateCompanyButton } from 'src/company/components/CreateCompanyButton';
import {
  ProjectFilter,
  ProjectArchiveStatus,
} from 'src/project/types/projects';
import { CopyProjectButton } from 'src/admin/components/user/projects/CopyProjectButton';
import { MobileFilterSummary } from './MobileFilterSummary';

const sessionKey = 'projectListSearchFilterAndSort';

export const ProjectList = () => {
  const { t } = useTranslation('project');
  const user = useUser();
  const project_id = import.meta.env.VITE_PROJECT_ID_TO_COPY;
  const shouldCloneFully = 'true';

  const { data: projects, isPending: isLoadingProjects } = useQuery({
    queryKey: ['projects'],
    queryFn: getAllProjects,
    initialData: [],
    staleTime: 0,
  });
  const { data: companies, isPending: isLoadingCompany } = useCompaniesQuery();

  const {
    search,
    setSearch,
    selectedTypeFilters,
    setSelectedTypeFilters,
    selectedStatusFilters,
    setSelectedStatusFilters,
    sortProperty,
    setSortProperty,
    sortDirection,
    setSortDirection,
  } = useSearchFilterAndSort();
  const {
    isOpen: isMobileFiltersModalOpen,
    open: openMobileFiltersModal,
    close: closeMobileFiltersModal,
  } = useModal();

  if (isLoadingProjects || isLoadingCompany) {
    return (
      <Spinner
        className="text-2xl"
        containerClassName="w-full my-[160px] text-center"
      />
    );
  }

  const { typeOptions, statusOptions } = getProjectFilterOptions(projects, t);

  const filteredProjects = filterProjectsByTypeAndStatus(
    projects,
    selectedTypeFilters,
    selectedStatusFilters,
  );

  const filteredAndSortedProjects = filterAndSortProjects(
    filteredProjects,
    search,
    sortDirection,
    sortProperty,
  );

  const projectsByCompany = groupProjectsByCompany(
    filteredAndSortedProjects,
    user,
    companies,
  );

  const handleTypeFiltersChange = (newFilterValues: ProjectFilter[]) => {
    const filterSet = [...new Set(newFilterValues)];
    setSelectedTypeFilters(filterSet);
    sessionStorage.setItem(
      sessionKey,
      JSON.stringify({
        selectedTypeFilters: filterSet,
        selectedStatusFilters,
        property: sortProperty,
        direction: sortDirection,
      }),
    );
  };

  const handleStatusFiltersChange = (
    newFilterValues: ProjectArchiveStatus[],
  ) => {
    const filterSet = [...new Set(newFilterValues)];
    setSelectedStatusFilters(filterSet);
    sessionStorage.setItem(
      sessionKey,
      JSON.stringify({
        selectedTypeFilters,
        selectedStatusFilters: filterSet,
        property: sortProperty,
        direction: sortDirection,
      }),
    );
  };

  const handleSortPropertyChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === 'createdAt') {
      setSortProperty('createdAt');
      sessionStorage.setItem(
        sessionKey,
        JSON.stringify({
          selectedTypeFilters,
          selectedStatusFilters,
          property: 'createdAt',
          direction: sortDirection,
        }),
      );
    } else {
      setSortProperty('name');
      sessionStorage.setItem(
        sessionKey,
        JSON.stringify({
          selectedTypeFilters,
          selectedStatusFilters,
          property: 'name',
          direction: sortDirection,
        }),
      );
    }
  };

  const handleToggleSortingDirection = () => {
    return setSortDirection((prev) => {
      const newDirection = prev === 'asc' ? 'desc' : 'asc';
      sessionStorage.setItem(
        sessionKey,
        JSON.stringify({
          selectedTypeFilters,
          selectedStatusFilters,
          property: sortProperty,
          direction: newDirection,
        }),
      );
      return newDirection;
    });
  };

  return (
    <>
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="w-full min-w-0 md:w-auto">
            <ProjectListActionBar
              search={search}
              onSearchChange={(e) => setSearch(e.currentTarget.value)}
              typeFilterOptions={typeOptions}
              statusFilterOptions={statusOptions}
              selectedTypeFilters={selectedTypeFilters}
              selectedStatusFilters={selectedStatusFilters}
              onTypeFilterChange={handleTypeFiltersChange}
              onStatusFilterChange={handleStatusFiltersChange}
              sortProperty={sortProperty}
              onSortPropertyChange={handleSortPropertyChange}
              sortDirection={sortDirection}
              onToggleSortDirection={handleToggleSortingDirection}
              isMobileFiltersModalOpen={isMobileFiltersModalOpen}
              onOpenMobileFiltersModal={openMobileFiltersModal}
              onCloseMobileFiltersModal={closeMobileFiltersModal}
            />
          </div>
          <MobileFilterSummary
            selectedTypeFilters={selectedTypeFilters}
            selectedStatusFilters={selectedStatusFilters}
            onOpenFiltersModal={openMobileFiltersModal}
          />
          <div className="hidden w-full min-w-0 min-[481px]:grid min-[481px]:grid-cols-2 min-[481px]:gap-2 md:flex md:w-auto md:items-end md:justify-start">
            <CopyProjectButton
              user_id={user._id}
              projectId={project_id}
              projectName=""
              shouldCloneFully={shouldCloneFully}
              size="sm"
              className="w-full md:h-10 md:w-auto md:px-4 md:text-base"
            />
            <CreateCompanyButton
              size="sm"
              className="w-full md:h-10 md:w-auto md:px-4 md:text-base"
            />
          </div>
        </div>
        <div className="space-y-12">
          {projectsByCompany.map((companyGroup) => (
            <ProjectListCompanySection
              key={companyGroup.name}
              companyGroup={companyGroup}
              disableCollapse={!!search}
            />
          ))}
        </div>
      </div>
      {user.showNewProjectModal && (
        <CreateFirstProjectModal
          firstName={user.name}
          personalCompanyId={user.personalCompany}
        />
      )}
    </>
  );
};

function useSearchFilterAndSort() {
  const projectListSort = sessionStorage.getItem(sessionKey);
  const value = projectListSort
    ? JSON.parse(projectListSort)
    : {
        selectedTypeFilters: [],
        selectedStatusFilters: [ProjectArchiveStatus.ACTIVE],
        property: 'name',
        direction: 'asc',
      };

  const [search, setSearch] = useState('');
  const [selectedTypeFilters, setSelectedTypeFilters] = useState<
    ProjectFilter[]
  >(value.selectedTypeFilters || []);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<
    ProjectArchiveStatus[]
  >(value.selectedStatusFilters || [ProjectArchiveStatus.ACTIVE]);
  const [sortProperty, setSortProperty] = useState<'name' | 'createdAt'>(
    value.property,
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    value.direction,
  );

  return {
    search,
    setSearch,
    selectedTypeFilters,
    setSelectedTypeFilters,
    selectedStatusFilters,
    setSelectedStatusFilters,
    sortProperty,
    setSortProperty,
    sortDirection,
    setSortDirection,
  };
}
